import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from 'components/icon/icon.component';
import { merge } from 'lodash';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DropdownActionButtonOptions, DropdownActionButtonComponent } from 'components/dropdown-action-button/dropdown-action-button.component';

export interface FormButtonOptions {
    submitDisabled?: boolean;
    submitLoading?: boolean;
    showSubmitButton?: boolean;
    showCancelButton?: boolean;
    showGoBackButton?: boolean;
    showDeleteButton?: boolean;
    enterSubmits?: boolean;
    submitTextKey?: string;
    deleteTextKey?: string;
    submitButtonDropdown?: SubmitButtonDropdown[];
    actionButton?: {
        textKey: string;
        class?: string;
        action: () => void;
    };
}

export interface SubmitButtonDropdown {
    textKey: string;
    action: () => void;
}

@Component({
    selector: 'app-form-buttons',
    templateUrl: './form-buttons.component.html',
    styleUrls: ['./form-buttons.component.scss'],
    standalone: true,
    imports: [
    CommonModule,
    MatMenuModule,
    TranslateModule,
    MatProgressSpinnerModule,
    IconComponent,
    DropdownActionButtonComponent,
],
})
export class FormButtonsComponent implements OnChanges, OnInit {
    static defaultOptions: Partial<FormButtonOptions> = {
        showSubmitButton: true,
        showCancelButton: true,
        showGoBackButton: false,
        showDeleteButton: false,
    };

    @Input() options: FormButtonOptions;
    @Input() submitDisabled: boolean;
    @Input() submitLoading: boolean;
    @Input() showCancelButton: boolean;
    @Input() showSubmitButton: boolean;
    @Input() showGoBackButton: boolean;
    @Input() showDeleteButton: boolean;
    @Input() enterSubmits: boolean;
    @Input() submitTextKey: string;
    @Input() deleteTextKey: string;
    @Input() submitButtonDropdown: SubmitButtonDropdown[];
    @Input() actionButton: FormButtonOptions['actionButton'];
    /**
     * Optional discriminator folded into the buttons' `data-testid`s so one particular form's buttons can
     * be addressed unambiguously. Without it the testids stay the shared `FormButtons-CancelButton` /
     * `FormButtons-SaveButton`; with e.g. `TableRowsDelete` they become `FormButtons-TableRowsDelete-…`.
     */
    @Input() testIdContext?: string;
    @Output() submitOutput = new EventEmitter<void>();
    @Output() deleteOutput = new EventEmitter<void>();
    @Output() cancelOutput = new EventEmitter<void>();
    @Output() backOutput = new EventEmitter<void>();

    dropdownActionButtonOptions?: DropdownActionButtonOptions;

    ngOnInit() {
        this.updateOptions();
    }

    ngOnChanges() {
        this.updateOptions();
    }

    private updateOptions() {
        this.options = merge(
            {}, // lodash merge mutates first object
            FormButtonsComponent.defaultOptions,
            this.options,
            {
                submitDisabled: this.submitDisabled,
                showCancelButton: this.showCancelButton,
                showSubmitButton: this.showSubmitButton,
                showGoBackButton: this.showGoBackButton,
                showDeleteButton: this.showDeleteButton,
                actionButton: this.actionButton,
                enterSubmits: this.enterSubmits,
                submitTextKey: this.submitTextKey,
                deleteTextKey: this.deleteTextKey,
                submitButtonDropdown: this.submitButtonDropdown,
            },
        );

        if (this.options.submitButtonDropdown) {
            this.dropdownActionButtonOptions = {
                mainButton: {
                    disabled: this.options.submitDisabled,
                    textKey: this.options.submitTextKey ?? 'generics.save',
                    onPress: () => this.submitOutput.emit(),
                    textFontSettings: {
                        size: 12,
                    },
                },
                mainButtonClass: 'blue-lt config-form-button spaced-right',
                dropdownButtons: this.options.submitButtonDropdown.map((button) => ({
                    textKey: button.textKey,
                    onPress: button.action,
                    textFontSettings: {
                        size: 12,
                    },
                })),
            };
        } else this.dropdownActionButtonOptions = undefined;
    }
}
